import React, { useContext, useEffect, useRef } from "react";
import { CartContext } from "./CartContext";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { gsap } from "gsap";
import { useUI } from "@/core/components/context/UIContext";

const CartDrawer: React.FC = () => {
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);
  const { isCartOpen, toggleCart } = useUI();
  const drawerRef = useRef<HTMLDivElement>(null);

  const totalPrice = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  useEffect(() => {
    if (drawerRef.current) {
      if (isCartOpen) {
        // Animate drawer in
        gsap.to(drawerRef.current, {
          x: 0,
          duration: 0.3,
          ease: "power2.out",
        });
      } else {
        // Animate drawer out
        gsap.to(drawerRef.current, {
          x: "100%",
          duration: 0.3,
          ease: "power2.in",
        });
      }
    }
  }, [isCartOpen]);

  // Don't render at all if cart is closed and empty
  if (!isCartOpen && !cartItems.length) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-[10000] transition-opacity duration-300 ${
          isCartOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={toggleCart}
      />

      {/* Cart drawer */}
      <div
        ref={drawerRef}
        className="fixed top-0 right-0 h-full w-full sm:w-96 bg-white z-[10001] shadow-xl transform translate-x-full"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex justify-between items-center border-b border-gray-200 p-4">
            <h2 className="text-xl font-semibold">Your Cart</h2>
            <button
              onClick={toggleCart}
              className="p-2 rounded-full hover:bg-gray-100"
              aria-label="Close cart"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Cart items */}
          <div className="flex-grow overflow-y-auto p-4">
            {cartItems.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">Your cart is empty</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex border-b pb-4">
                    <div className="w-20 h-20 bg-gray-100 rounded-md overflow-hidden mr-4">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          No image
                        </div>
                      )}
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-medium">{item.name}</h3>
                      <p className="text-cannabis-green-600 font-bold">
                        ${item.price.toFixed(2)}
                      </p>
                      <div className="flex items-center mt-2">
                        <button
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              Math.max(1, item.quantity - 1)
                            )
                          }
                          className="w-8 h-8 border rounded-md flex items-center justify-center"
                        >
                          -
                        </button>
                        <span className="mx-2">{item.quantity}</span>
                        <button
                          onClick={() =>
                            updateQuantity(item.id, item.quantity + 1)
                          }
                          className="w-8 h-8 border rounded-md flex items-center justify-center"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="text-gray-400 hover:text-gray-600"
                      aria-label="Remove item"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer with totals and checkout button */}
          {cartItems.length > 0 && (
            <div className="border-t border-gray-200 p-4">
              <div className="flex justify-between mb-4">
                <span className="font-medium">Total</span>
                <span className="font-bold">${totalPrice.toFixed(2)}</span>
              </div>
              <button className="w-full bg-cannabis-green-500 text-white py-3 rounded-md font-medium hover:bg-cannabis-green-600 transition-colors">
                Checkout
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartDrawer;
